import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { MessageCircle, X, Send } from "lucide-react";
import { useLocation } from "react-router-dom";

export default function SupportWidget() {
  const [open, setOpen] = useState(false);
  const [started, setStarted] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const lastFetch = useRef(null);
  const listRef = useRef(null);
  const location = useLocation();
  const hidden = location.pathname.startsWith("/admin/support");

  // Check existing thread via cookie on first open
  useEffect(() => {
    if (!open || hidden) return;
    api
      .get("/support/messages")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) {
          setStarted(true);
          setMessages(r.data);
          lastFetch.current = r.data[r.data.length - 1].created_at;
        }
      })
      .catch(() => {});
  }, [open, hidden]);

  // Poll while open
  useEffect(() => {
    if (!open || !started || hidden) return;
    const t = setInterval(async () => {
      try {
        const params = lastFetch.current ? { since: lastFetch.current } : {};
        const { data } = await api.get("/support/messages", { params });
        if (Array.isArray(data) && data.length > 0) {
          setMessages((prev) => [...prev, ...data]);
          lastFetch.current = data[data.length - 1].created_at;
        }
      } catch {}
    }, 4000);
    return () => clearInterval(t);
  }, [open, started, hidden]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  if (hidden) return null;

  const start = async (e) => {
    e?.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/support/start", { name, email, message });
      const { data } = await api.get("/support/messages");
      setMessages(data || []);
      if (data?.length) lastFetch.current = data[data.length - 1].created_at;
      setStarted(true);
      setMessage("");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!message.trim()) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/support/message", { thread_id: "", body: message });
      setMessages((prev) => [...prev, data]);
      lastFetch.current = data.created_at;
      setMessage("");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="support-open"
          onClick={() => setOpen(true)}
          style={{ bottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))" }}
          className="fixed right-3 sm:right-6 z-[60] w-12 h-12 sm:w-14 sm:h-14 bg-eduhu-claret hover:bg-eduhu-dark transition-all duration-300 text-eduhu-bg flex items-center justify-center shadow-2xl pulse-gold"
          aria-label="Open support"
        >
          <MessageCircle size={22} strokeWidth={1.5} />
        </button>
      )}

      {open && (
        <div
          data-testid="support-widget"
          className="fixed right-3 sm:right-6 z-[60] w-[360px] max-w-[calc(100vw-1.5rem)] bg-eduhu-bg2 border border-eduhu-border shadow-2xl flex flex-col"
          style={{ height: "min(520px, calc(100dvh - 2.5rem - env(safe-area-inset-bottom, 0px) - env(safe-area-inset-top, 0px)))", bottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))" }}
        >
          <header className="flex items-center justify-between px-4 py-3 bg-eduhu-dark text-eduhu-bg">
            <div>
              <div className="overline text-eduhu-gold">EDUHUB · Support</div>
              <div className="font-serif text-lg leading-tight">We're here to help.</div>
            </div>
            <button
              data-testid="support-close"
              onClick={() => setOpen(false)}
              className="p-1 hover:bg-white/10 transition-colors"
            >
              <X size={18} />
            </button>
          </header>

          {!started ? (
            <form onSubmit={start} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="support-start-form">
              <div className="text-sm text-eduhu-inkSoft">
                Tell us how to reach you — a human will reply ASAP. If we're not around, we'll email you back.
              </div>
              <input
                data-testid="support-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name (optional)"
                className="w-full bg-eduhu-bg border border-eduhu-border px-3 py-2 text-sm"
              />
              <input
                data-testid="support-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email for follow-up (optional)"
                className="w-full bg-eduhu-bg border border-eduhu-border px-3 py-2 text-sm"
              />
              <textarea
                data-testid="support-message"
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="How can we help?"
                rows={4}
                className="w-full bg-eduhu-bg border border-eduhu-border px-3 py-2 text-sm"
              />
              {err && <div className="text-sm text-eduhu-error">{err}</div>}
              <button data-testid="support-start-btn" disabled={busy} className="eduhu-btn-primary w-full justify-center">
                {busy ? "Sending…" : "Start chat"} <Send size={14} />
              </button>
            </form>
          ) : (
            <>
              <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="support-thread">
                {messages.length === 0 && (
                  <div className="text-xs text-eduhu-inkSoft text-center py-8">Conversation starts here…</div>
                )}
                {messages.map((m) => {
                  const mine = m.author === "guest";
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className="max-w-[85%]">
                        <div className="text-[10px] uppercase tracking-widest text-eduhu-inkSoft mb-1">
                          {mine ? "you" : m.name || "support"} · {new Date(m.created_at).toLocaleTimeString()}
                        </div>
                        <div
                          className={`px-3 py-2 leading-snug text-sm whitespace-pre-wrap break-words ${
                            mine ? "bg-eduhu-dark text-eduhu-bg" : "bg-eduhu-gold/15 border border-eduhu-gold/40"
                          }`}
                        >
                          {m.body}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={send} className="flex gap-2 p-3 border-t border-eduhu-border bg-eduhu-bg3/30">
                <input
                  data-testid="support-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type a message…"
                  className="flex-1 bg-eduhu-bg2 border border-eduhu-border px-3 py-2 text-sm focus:outline-none focus:border-eduhu-dark"
                />
                <button
                  data-testid="support-send"
                  type="submit"
                  disabled={busy || !message.trim()}
                  className="eduhu-btn-primary !py-2 !px-3 text-sm"
                >
                  <Send size={14} />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
